import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Divider, Form, Input, Select, Typography } from 'antd';
import { Store } from 'rc-field-form/lib/interface';
import ServerPricingInfo from './ServerPricingInfo';
import { BYTES_PER_GB, INITIAL_SERVER_SETUP_SIZE_STORAGE_KEY, INITIAL_SERVER_SETUP_STORAGE_KEY } from './NewServerStart';

declare global {
	interface Window {
		Mollie?: (profileId: string, options?: { testmode?: boolean }) => {
			createComponent: (type: string, options?: { styles?: any }) => {
				mount: (selector: string) => void;
				unmount?: () => void;
			};
		};
	}
}

type BillingInfo = {
	title: string;
	given_name: string;
	family_name: string;
	organization_name: string;
	street_and_number: string;
	street_additional: string;
	postal_code: string;
	city: string;
	region: string;
	country: string;
	vat_id: string;
};

const COUNTRY_OPTIONS: Array<{ code: string; name: string }> = [
	{ code: 'AR', name: 'Argentina' },
	{ code: 'AT', name: 'Austria' },
	{ code: 'AU', name: 'Australia' },
	{ code: 'BE', name: 'Belgium' },
	{ code: 'BG', name: 'Bulgaria' },
	{ code: 'BR', name: 'Brazil' },
	{ code: 'CA', name: 'Canada' },
	{ code: 'CH', name: 'Switzerland' },
	{ code: 'CN', name: 'China' },
	{ code: 'CY', name: 'Cyprus' },
	{ code: 'CZ', name: 'Czechia' },
	{ code: 'DE', name: 'Germany' },
	{ code: 'DK', name: 'Denmark' },
	{ code: 'EE', name: 'Estonia' },
	{ code: 'ES', name: 'Spain' },
	{ code: 'FI', name: 'Finland' },
	{ code: 'FR', name: 'France' },
	{ code: 'GB', name: 'United Kingdom' },
	{ code: 'GR', name: 'Greece' },
	{ code: 'HR', name: 'Croatia' },
	{ code: 'HU', name: 'Hungary' },
	{ code: 'IE', name: 'Ireland' },
	{ code: 'IN', name: 'India' },
	{ code: 'IT', name: 'Italy' },
	{ code: 'JP', name: 'Japan' },
	{ code: 'LT', name: 'Lithuania' },
	{ code: 'LU', name: 'Luxembourg' },
	{ code: 'LV', name: 'Latvia' },
	{ code: 'MT', name: 'Malta' },
	{ code: 'MX', name: 'Mexico' },
	{ code: 'NL', name: 'Netherlands' },
	{ code: 'NO', name: 'Norway' },
	{ code: 'NZ', name: 'New Zealand' },
	{ code: 'PL', name: 'Poland' },
	{ code: 'PT', name: 'Portugal' },
	{ code: 'RO', name: 'Romania' },
	{ code: 'SE', name: 'Sweden' },
	{ code: 'SI', name: 'Slovenia' },
	{ code: 'SK', name: 'Slovakia' },
	{ code: 'US', name: 'United States' }
];

const Payment: React.FC = () => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let cardForm: { mount: (selector: string) => void; unmount?: () => void } | null = null;
		let script = document.getElementById('mollie-js-sdk') as HTMLScriptElement | null;

		const initializeMollieCard = () => {
			if (typeof window.Mollie !== 'function') {
				return;
			}

			const mollie = window.Mollie('pfl_pnYJ7Er8tJ', { testmode: true });

			const options = {
				styles: {
					base: {
						backgroundColor: '#ffffff',
						color: '#000000',
						fontSize: '16px',
						lineHeight: '24px',
						'::placeholder': {
							color: '#000000'
						}
					},
					invalid: {
						color: '#d25f5f'
					}
				}
			};

			cardForm = mollie.createComponent('card', options);
			cardForm.mount('#mollie-card');
			(window as any).curr_mollie = mollie;
		};

		if (script) {
			initializeMollieCard();
		} else {
			script = document.createElement('script');
			script.id = 'mollie-js-sdk';
			script.src = 'https://js.mollie.com/v1/mollie.js';
			script.async = true;
			script.onload = initializeMollieCard;
			document.body.appendChild(script);
		}

		return () => {
			if (cardForm?.unmount) {
				cardForm.unmount();
			}
		};
	}, []);

	const onFinish = async (values: Store) => {
		const formValues = values as BillingInfo;
		setLoading(true);
		setSuccess(null);
		setError(null);

		try {
			const uuid = localStorage.getItem(INITIAL_SERVER_SETUP_STORAGE_KEY);
			if (!uuid) {
				setError('Missing initial server setup UUID. Please restart the server setup flow.');
				return;
			}

			var { token, error } = await (window as any).curr_mollie.createToken();

			if (error) {
				setError('Error creating payment token: ' + error.message);
				// Something wrong happened while creating the token. Handle this situation gracefully.
				return;
			}

			const billingInfo: BillingInfo = {
				...formValues,
				country: formValues.country.toUpperCase()
			};

			const formData = new FormData();
			formData.append('uuid', uuid);
			formData.append('billing_info', JSON.stringify(billingInfo));
			formData.append('payment_token', token);

			const response = await fetch('/api/create_payment', {
				method: 'POST',
				credentials: 'include',
				body: formData
			});

			const data = await response.json();

			if (!response.ok || data.ok !== true || typeof data.payment_url !== 'string') {
				setError(data.err || data.error || 'Could not create payment. Please try again.');
				return;
			}

			setSuccess('Redirecting to payment...');
			window.location.href = data.payment_url;
			return;
		} catch (e) {
			setError('Connection issue while creating payment. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	return (
		<div
			style={{
				minHeight: '100vh',
				padding: '32px 20px',
				background:
					'radial-gradient(circle at top left, rgba(22, 78, 99, 0.22), transparent 30%), radial-gradient(circle at top right, rgba(15, 118, 110, 0.16), transparent 24%), linear-gradient(180deg, #07131d 0%, #0f1e2a 42%, #f4f7fb 42%, #f4f7fb 100%)'
			}}
		>
			<div style={{ maxWidth: 1200, margin: '0 auto' }}>
				<div style={{ marginBottom: 24, color: '#eaf3fb' }}>
					<Typography.Text style={{ color: 'rgba(234, 243, 251, 0.7)', textTransform: 'uppercase', letterSpacing: 1.4 }}>
						Secure checkout
					</Typography.Text>
					<Typography.Title level={1} style={{ color: '#ffffff', marginTop: 8, marginBottom: 8 }}>
						Complete your payment details
					</Typography.Title>
					<Typography.Paragraph style={{ color: 'rgba(234, 243, 251, 0.82)', maxWidth: 760, fontSize: 16, lineHeight: 1.8, marginBottom: 0 }}>
						Enter the billing information used for your invoice and continue to the payment provider.
					</Typography.Paragraph>
				</div>

				<div
					style={{
						display: 'grid',
						gridTemplateColumns: 'minmax(0, 1.05fr) minmax(320px, 0.95fr)',
						gap: 24,
						alignItems: 'start'
					}}
				>
					<Card
						style={{ borderRadius: 20, boxShadow: '0 24px 80px rgba(3, 11, 21, 0.12)' }}
						title={<Typography.Title level={3} style={{ margin: 0 }}>Billing information</Typography.Title>}
					>
						{error && (
							<Alert style={{ marginBottom: 16 }} type="error" message={error} showIcon />
						)}

						{success && (
							<Alert style={{ marginBottom: 16 }} type="success" message={success} showIcon />
						)}

						<Form
							form={form}
							layout="vertical"
							onFinish={onFinish}
							initialValues={{
								title: '',
								given_name: '',
								family_name: '',
								organization_name: '',
								street_and_number: '',
								street_additional: '',
								postal_code: '',
								city: '',
								region: '',
								country: undefined,
								vat_id: ''
							}}
						>
							<div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 }}>
								<Form.Item name="title" label="Title">
									<Input placeholder="Mr / Ms / Dr" />
								</Form.Item>

								<Form.Item
									name="given_name"
									label="Given name"
									rules={[{ required: true, message: 'Please enter the given name.' }]}
								>
									<Input placeholder="John" />
								</Form.Item>

								<Form.Item
									name="family_name"
									label="Family name"
									rules={[{ required: true, message: 'Please enter the family name.' }]}
								>
									<Input placeholder="Doe" />
								</Form.Item>

								<Form.Item
									name="organization_name"
									label="Organization name"
									rules={[{ required: true, message: 'Please enter the organization name.' }]}
								>
									<Input placeholder="Acme Corp" />
								</Form.Item>

								<Form.Item
									name="street_and_number"
									label="Street and number"
									rules={[{ required: true, message: 'Please enter street and number.' }]}
								>
									<Input placeholder="Main Street 42" />
								</Form.Item>

								<Form.Item name="street_additional" label="Street additional">
									<Input placeholder="Apartment, suite, floor (optional)" />
								</Form.Item>

								<Form.Item
									name="postal_code"
									label="Postal code"
									rules={[{ required: true, message: 'Please enter the postal code.' }]}
								>
									<Input placeholder="12345" />
								</Form.Item>

								<Form.Item
									name="city"
									label="City"
									rules={[{ required: true, message: 'Please enter the city.' }]}
								>
									<Input placeholder="Berlin" />
								</Form.Item>

								<Form.Item
									name="region"
									label="Region"
									rules={[{ required: true, message: 'Please enter the region.' }]}
								>
									<Input placeholder="State / Province" />
								</Form.Item>

								<Form.Item
									name="country"
									label="Country"
									rules={[
										{ required: true, message: 'Please select a country.' },
										{
											pattern: /^[A-Z]{2}$/,
											message: 'Country must be in ISO 3166-1 alpha-2 format.'
										}
									]}
								>
									<Select placeholder="Select country" showSearch optionFilterProp="children">
										{COUNTRY_OPTIONS.map((country) => (
											<Select.Option key={country.code} value={country.code}>
												{country.name} ({country.code})
											</Select.Option>
										))}
									</Select>
								</Form.Item>

								<Form.Item
									name="vat_id"
									label="VAT ID"
									rules={[{ required: true, message: 'Please enter the VAT ID.' }]}
								>
									<Input placeholder="DE123456789" />
								</Form.Item>

								<Form.Item noStyle shouldUpdate>
									{() => {
										const storage_size = localStorage.getItem(INITIAL_SERVER_SETUP_SIZE_STORAGE_KEY);
										if (!storage_size) {
											setError('Missing initial server setup size. Please restart the server setup flow.');
											return;
										}
										const currentSizeGb = Math.round(Number(storage_size) / BYTES_PER_GB);
										return <ServerPricingInfo sizeGb={currentSizeGb} />;
									}}
								</Form.Item>
							</div>

							<Divider style={{ margin: '8px 0 20px' }} />

							<div style={{ marginBottom: 20 }}>
								<Typography.Text style={{ display: 'block', marginBottom: 8, color: '#a0a0a0' }}>
									Credit card details
								</Typography.Text>
								<div
									style={{
										padding: 12,
										border: '1px solid #d9e1ea',
										borderRadius: 10,
										backgroundColor: '#ffffff',
										minHeight: 54
									}}
								>
									<div id="mollie-card" />
								</div>
							</div>

							<Form.Item style={{ marginBottom: 0 }}>
								<Button type="primary" htmlType="submit" loading={loading} block size="large">
									Create for a fee
								</Button>
							</Form.Item>
						</Form>
					</Card>

					<Card
						style={{ borderRadius: 20, boxShadow: '0 24px 80px rgba(3, 11, 21, 0.10)', background: 'linear-gradient(180deg, #0f1720 0%, #152433 100%)', color: '#f4f7fb' }}
						bodyStyle={{ padding: 28 }}
					>
						<Typography.Text style={{ color: 'rgba(244, 247, 251, 0.65)', textTransform: 'uppercase', letterSpacing: 1.2 }}>
							What happens next
						</Typography.Text>
						<Typography.Title level={3} style={{ color: '#ffffff', marginTop: 8 }}>
							You’ll be redirected to a secure payment provider.
						</Typography.Title>
						<Typography.Paragraph style={{ color: 'rgba(244, 247, 251, 0.8)', lineHeight: 1.8, fontSize: 15 }}>
							We use the billing information above to generate your checkout session and invoice details.
						</Typography.Paragraph>

						<div style={{ display: 'grid', gap: 14, marginTop: 20 }}>
							<div style={{ padding: 16, borderRadius: 14, background: 'rgba(255, 255, 255, 0.06)' }}>
								<Typography.Text style={{ color: '#8bd3ff', display: 'block', marginBottom: 4 }}>1. Review</Typography.Text>
								<Typography.Text style={{ color: '#ffffff' }}>Check the billing details before proceeding.</Typography.Text>
							</div>
							<div style={{ padding: 16, borderRadius: 14, background: 'rgba(255, 255, 255, 0.06)' }}>
								<Typography.Text style={{ color: '#8bd3ff', display: 'block', marginBottom: 4 }}>2. Redirect</Typography.Text>
								<Typography.Text style={{ color: '#ffffff' }}>Continue to the external checkout page.</Typography.Text>
							</div>
							<div style={{ padding: 16, borderRadius: 14, background: 'rgba(255, 255, 255, 0.06)' }}>
								<Typography.Text style={{ color: '#8bd3ff', display: 'block', marginBottom: 4 }}>3. Activate</Typography.Text>
								<Typography.Text style={{ color: '#ffffff' }}>Your server setup continues automatically after payment.</Typography.Text>
							</div>
						</div>
					</Card>
				</div>
			</div>
		</div>
	);
};

export default Payment;
